// Off The Clock — game data
// A different shape from the other levels, on purpose: instead of money,
// the resource here is Hours — the limited time left after school or a job.
// Four stages: Observation (spot real patterns) -> Direction (pick one and
// commit real hours to it) -> The Build (defend a 90-day effort) -> Debrief.
//
// This level adapts source material about leverage and influence for a
// teen/young-adult audience. Anything about influencing people is reframed
// around genuine usefulness, reliability, and reputation — not manipulation.

export const HOURS_BUDGET = 12; // off-the-clock hours available per week

// ---------------------------------------------------------------------------
// STAGE 1 — OBSERVATION: six real-feeling situations. First question: is
// this an actual recurring pattern worth solving, or just a one-off
// annoyance? Second question (only if "worth solving"): which kind of
// leverage would solving it build?
// ---------------------------------------------------------------------------
export const LEVERAGE_TYPES = [
  { id: "INFORMATION", label: "Information", icon: "📓", description: "Knowledge you've already organized that others keep asking for." },
  { id: "SYSTEMS", label: "Systems", icon: "⚙️", description: "A repeatable process that removes recurring friction." },
  { id: "PEOPLE", label: "People", icon: "🤝", description: "Relationships and trust that open doors over time." },
  { id: "CAPITAL", label: "Capital", icon: "💰", description: "Money put to work — usually the last domino, not the first." },
];

export const PATTERNS = [
  {
    id: "study-guide",
    text: "Three different classmates DM you this week, all asking for the same study guide you already made for one class.",
    correctWorthSolving: true,
    correctType: "INFORMATION",
    note: "You already did the hard part — organizing it. Now it just needs a home other than your DMs.",
  },
  {
    id: "pizza-split",
    text: "Your friend group manually re-splits the pizza bill every single time, and the math is wrong more often than not.",
    correctWorthSolving: true,
    correctType: "SYSTEMS",
    note: "One recurring annoyance, one repeatable fix — that's what a system is for.",
  },
  {
    id: "mentors-nearby",
    text: "You keep running into the same handful of older students who all work at places you'd genuinely love to learn from.",
    correctWorthSolving: true,
    correctType: "PEOPLE",
    note: "Proximity isn't the same as a relationship — but it's the raw material for one.",
  },
  {
    id: "idle-savings",
    text: "Birthday and gift money has been piling up in a checking account, doing nothing.",
    correctWorthSolving: true,
    correctType: "CAPITAL",
    note: "Real, but worth noticing: capital works best rewarding something that already works — not as the very first move.",
  },
  {
    id: "vending-machine",
    text: "The vending machine at school is out of your favorite snack sometimes.",
    correctWorthSolving: false,
    correctType: null,
    note: "Mildly annoying, but it doesn't repeat in a way that adds up to anything. Not every irritation is an opportunity.",
  },
  {
    id: "bad-day",
    text: "You had one day this week where everything just felt hard.",
    correctWorthSolving: false,
    correctType: null,
    note: "A single bad day isn't a pattern — it's just a day. Patterns need repetition before they mean anything.",
  },
];

// ---------------------------------------------------------------------------
// STAGE 2 — DIRECTION: choose the sharpest purpose statement (specific and
// dated beats vague and motivational), then allocate a fixed weekly Hours
// budget across three buckets.
// ---------------------------------------------------------------------------
export const PURPOSE_OPTIONS = [
  { id: "vague1", text: "I want to make some extra money somehow.", quality: "vague" },
  { id: "vague2", text: "I'm going to work really hard and see what happens.", quality: "vague" },
  { id: "sharp", text: "Turn my study guides into a shared notes library for my grade within 30 days.", quality: "sharp" },
  { id: "vague3", text: "I should probably start a side hustle at some point.", quality: "vague" },
];

export const HOUR_BUCKETS = [
  { id: "learn", label: "Learn", icon: "📚", flavor: "Building the skill and gathering the information this needs." },
  { id: "build", label: "Build", icon: "🛠️", flavor: "Actually making the thing — the system, the artifact, the first version." },
  { id: "connect", label: "Connect", icon: "🤝", flavor: "Talking to the people this could help, or who've done it before." },
];

// ---------------------------------------------------------------------------
// STAGE 3 — THE BUILD: six moments across a compressed 90-day cycle. Effects
// feed the Debrief's scoring, not a dollar simulation — this level tracks
// momentum and trust, not cash.
// ---------------------------------------------------------------------------
export const ENCOUNTERS = [
  {
    id: "e1",
    phase: "Weeks 1\u20132 \u00b7 Observation",
    label: "The Boring Middle",
    text: "Logging three small patterns a day felt exciting on day one. On day twelve, it just feels tedious.",
    choices: [
      { id: "keep-logging", label: "Keep logging anyway \u2014 even the boring days count", tag: "good", result: "The habit held. Most people quit right around here, which is exactly why it's worth something." },
      { id: "skip-week", label: "Skip it this week, catch up later", tag: "bad", result: "\u201cLater\u201d has a way of not arriving. The habit slipped." },
    ],
  },
  {
    id: "e2",
    phase: "Weeks 3\u20134 \u00b7 Intersection",
    label: "Connecting the Dots",
    text: "You've got a skill (organizing notes clearly) and a pattern (classmates always asking for them). They're just sitting next to each other, unconnected.",
    choices: [
      { id: "combine", label: "Combine them into one specific offer: a shared notes library, open by a set date", tag: "good", result: "A skill plus a pattern, actually joined together, is what turns into a real offer." },
      { id: "separate", label: "Keep treating them as two separate, someday ideas", tag: "bad", result: "Two loose ideas next to each other never turned into one real thing." },
    ],
  },
  {
    id: "e3",
    phase: "Week 4 \u00b7 Artifact",
    label: "Ship It or Polish It",
    text: "You've got a rough version ready \u2014 genuinely useful, but not close to perfect.",
    choices: [
      { id: "ship", label: "Ship it within the next 72 hours, imperfect and all", tag: "good", result: "Real people started using it \u2014 and told you exactly what to fix next, which no amount of solo polishing could have." },
      { id: "polish", label: "Keep polishing alone for another month before anyone sees it", tag: "bad", result: "A month later, it was more polished \u2014 and nobody had used it yet, including to find out what actually needed fixing." },
    ],
  },
  {
    id: "e4",
    phase: "Ongoing \u00b7 Trust",
    label: "The Mentor Meeting",
    text: "An adult who runs a small tutoring business offers to sit down and look at what you built.",
    choices: [
      { id: "listen-first", label: "Ask what problem they need solved, and see if you can help with that too", tag: "good", result: "Being useful to someone first is what actually opens doors \u2014 it's not a trick, it's just true." },
      { id: "pitch-only", label: "Spend the whole meeting talking about your own plans", tag: "bad", result: "They were polite, but the meeting didn't turn into anything." },
    ],
  },
  {
    id: "e5",
    phase: "Ongoing \u00b7 Resilience",
    label: "The Pull Back to Easy",
    text: "It's been a rough week. Going back to just relaxing after school sounds a lot easier than keeping this going.",
    choices: [
      { id: "small-step", label: "Do one small step today anyway \u2014 an hour, not a leap", tag: "good", result: "Small and consistent beat the urge to quit. The habit survived a hard week." },
      { id: "week-off", label: "Take the week off, \u201cjust this once\u201d", tag: "bad", result: "\u201cJust this once\u201d is how most people's momentum quietly ends." },
    ],
  },
  {
    id: "e6",
    phase: "Ongoing \u00b7 Iteration",
    label: "Real Feedback",
    text: "A few people tried what you built. One said one specific part was confusing.",
    choices: [
      { id: "fix-it", label: "Fix that one specific thing \u2014 real feedback beats your own certainty", tag: "good", result: "One small fix, and the confusing part stopped being confusing." },
      { id: "ignore", label: "Ignore it \u2014 you're confident your original version is right", tag: "bad", result: "The same complaint came up again a week later, from someone else." },
    ],
  },
];
